import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FaEdit, FaPlus, FaTrash, FaLock } from "react-icons/fa";
import Sidebar from "../../../components/Sidebar/Sidebar";
import Modal from "../../../components/Modal/Modal";
import "../../../components/AdminList/AdminList.css";
import "./Roles.css";
import { AuthContext } from "../../../context/Authcontext";
import { getRoles, getPermissionCatalogue, createRole, updateRole, deleteRole } from "../../../utils/api";

const ACTION_ORDER = ["view", "create", "update", "delete"];
const emptyForm = { name: "", description: "", permissions: [] };

export default function Roles() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [roleList, catalogue] = await Promise.all([getRoles(), getPermissionCatalogue()]);
      setRoles(Array.isArray(roleList) ? roleList : []);
      setModules(catalogue?.modules || []);
      setError("");
    } catch (err) {
      setError(err.message || "Kon rollen niet laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Extra actions (e.g. "Wachtwoord resetten") are shown after the CRUD columns.
  const extraActions = useMemo(() => {
    const seen = new Map();
    modules.forEach((m) => m.actions.forEach((a) => {
      if (!ACTION_ORDER.includes(a.action) && !seen.has(a.action)) seen.set(a.action, a.label);
    }));
    return [...seen.entries()].map(([action, label]) => ({ action, label }));
  }, [modules]);

  const openCreate = () => {
    setEditingRole(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({ name: role.name, description: role.description || "", permissions: role.permissions || [] });
    setModalOpen(true);
  };

  const togglePermission = (key) => {
    setForm((prev) => {
      const has = prev.permissions.includes(key);
      let next = has ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key];
      const [moduleKey, action] = key.split(".");
      // Any right on a module implies being able to see it.
      if (!has && action !== "view") {
        const viewKey = `${moduleKey}.view`;
        const mod = modules.find((m) => m.key === moduleKey);
        if (mod?.actions.some((a) => a.key === viewKey) && !next.includes(viewKey)) next.push(viewKey);
      }
      // Removing "view" removes the other rights of that module as well.
      if (has && action === "view") next = next.filter((p) => !p.startsWith(`${moduleKey}.`));
      return { ...prev, permissions: next };
    });
  };

  const toggleRow = (mod) => {
    const keys = mod.actions.map((a) => a.key).filter((k) => isAdmin || k !== "roles.manage");
    setForm((prev) => {
      const allOn = keys.every((k) => prev.permissions.includes(k));
      const next = allOn
        ? prev.permissions.filter((p) => !keys.includes(p))
        : [...new Set([...prev.permissions, ...keys])];
      return { ...prev, permissions: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingRole) await updateRole(editingRole._id, form);
      else await createRole(form);
      setModalOpen(false);
      await load();
    } catch (err) {
      alert(err.message || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Rol "${role.name}" verwijderen?`)) return;
    try {
      await deleteRole(role._id);
      await load();
    } catch (err) {
      alert(err.message || "Verwijderen mislukt");
    }
  };

  const renderCheckbox = (mod, action) => {
    const entry = mod.actions.find((a) => a.action === action);
    if (!entry) return <td key={action} className="perm-na">–</td>;
    const locked = entry.key === "roles.manage" && !isAdmin;
    return (
      <td key={action}>
        <input
          type="checkbox"
          checked={form.permissions.includes(entry.key)}
          onChange={() => togglePermission(entry.key)}
          disabled={locked}
          title={locked ? "Alleen een admin kan dit recht toekennen" : entry.label}
        />
      </td>
    );
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <div className="admin-list-container">
          <div className="admin-list-header">
            <h2>Rollen &amp; rechten</h2>
            <div className="header-actions">
              <button className="add-btn" onClick={openCreate}>
                <FaPlus /> Nieuwe rol
              </button>
            </div>
          </div>

          <p className="roles-intro">
            Maak rollen aan voor interne medewerkers (bijv. Support, Redactie) en bepaal per onderdeel wat zij mogen.
            Wijs een rol toe via <strong>Users</strong> → rol <em>Medewerker</em>. De rol <strong>Admin</strong> heeft altijd
            alle rechten en staat daarom niet in deze lijst. Wijzigingen gelden binnen 30 seconden.
          </p>

          {loading && <div className="admin-list-loading"><div className="spinner"></div></div>}
          {error && <div className="admin-list-error">{error}</div>}

          {!loading && !error && (
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Naam</th>
                    <th>Omschrijving</th>
                    <th>Rechten</th>
                    <th>Medewerkers</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length === 0 && (
                    <tr><td colSpan={5} className="no-data">Nog geen rollen</td></tr>
                  )}
                  {roles.map((role) => (
                    <tr key={role._id}>
                      <td>
                        {role.name} {role.isSystem && <FaLock title="Systeemrol (kan niet verwijderd worden)" />}
                      </td>
                      <td>{role.description || "-"}</td>
                      <td>{role.permissions?.length || 0}</td>
                      <td>{role.userCount || 0}</td>
                      <td className="actions">
                        <button className="edit-btn" title="Bewerken" onClick={() => openEdit(role)}><FaEdit /></button>
                        {!role.isSystem && (
                          <button className="delete-btn" title="Verwijderen" onClick={() => handleDelete(role)}><FaTrash /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={editingRole ? `Rol bewerken: ${editingRole.name}` : "Nieuwe rol"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-group">
              <label>Naam *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
                placeholder="Bijv. Support"
              />
            </div>
            <div className="form-group">
              <label>Omschrijving</label>
              <textarea
                rows="2"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Waarvoor is deze rol bedoeld?"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Rechten</h3>
            <div className="table-container">
              <table className="admin-table perm-table">
                <thead>
                  <tr>
                    <th>Onderdeel</th>
                    <th>Bekijken</th>
                    <th>Aanmaken</th>
                    <th>Wijzigen</th>
                    <th>Verwijderen</th>
                    {extraActions.map((a) => <th key={a.action}>{a.label}</th>)}
                    <th>Alles</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((mod) => {
                    const keys = mod.actions.map((a) => a.key);
                    const allOn = keys.every((k) => form.permissions.includes(k));
                    return (
                      <tr key={mod.key}>
                        <td>{mod.label}</td>
                        {ACTION_ORDER.map((action) => renderCheckbox(mod, action))}
                        {extraActions.map((a) => renderCheckbox(mod, a.action))}
                        <td>
                          <input type="checkbox" checked={allOn} onChange={() => toggleRow(mod)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Annuleren</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
